import { PartItem } from '@cutr/constants/cutlist';
import { create } from 'zustand';

import { useActiveGroup, useActiveGroupParts } from './materialsGroup';

type NestingErrors = {
  nestingErrors: string[];
  addError(error: string): void;
  resetErrors(): void;
};

export const useNestingErrorStore = create<NestingErrors>((set) => ({
  nestingErrors: [],
  addError: (error) =>
    set((state) => ({ nestingErrors: [...state.nestingErrors, error] })),
  resetErrors: () => set(() => ({ nestingErrors: [] })),
}));

type PartError = {
  partItemId: string;
  field: keyof PartItem | 'nesting';
  errorMessage: string;
};

type PartWarning = {
  partItemId: string;
  message: string;
};

type PartErrors = {
  partErrors: PartError[];
  partWarnings: PartWarning[];
  setPartWarning(partItemId: string, message: string | null): void;
  resetPartsNestingError(partItemIds?: string[]): void;
  addPartError(
    partItemId: string,
    field: PartError['field'],
    error: string
  ): void;
  removePartErrorsByField(partItemId: string, field: PartError['field']): void;
  removePartErrors(partItemId: string): void;
};

export const usePartsErrorStore = create<PartErrors>((set) => ({
  partErrors: [],
  partWarnings: [],
  setPartWarning: (partItemId, message) =>
    set((state) => ({
      partWarnings: state.partWarnings
        .filter((warning) => warning.partItemId !== partItemId)
        .concat(message ? { partItemId, message } : []),
    })),
  resetPartsNestingError: (partItemIds) =>
    set((state) => ({
      ...state,
      partErrors: state.partErrors.filter(
        (pE) =>
          pE.field !== 'nesting' ||
          (partItemIds !== undefined && !partItemIds.includes(pE.partItemId))
      ),
    })),
  addPartError: (partItemId, field, errorMessage) =>
    set((state) => ({
      partErrors: state.partErrors
        .filter((err) => err.partItemId !== partItemId || err.field !== field)
        .concat({
          partItemId,
          field,
          errorMessage,
        }),
    })),
  removePartErrorsByField: (partItemId: string, field: keyof PartItem) =>
    set((state) => ({
      partErrors: state.partErrors.filter(
        (partError) =>
          partError.partItemId !== partItemId || partError.field !== field
      ),
    })),
  removePartErrors: (partItemId: string) =>
    set((state) => ({
      partErrors: state.partErrors.filter(
        (partError) => partError.partItemId !== partItemId
      ),
      partWarnings: state.partWarnings.filter(
        (warning) => warning.partItemId !== partItemId
      ),
    })),
}));

export const useCurrentGroupPartErrors = () => {
  const { partErrors } = usePartsErrorStore();
  const parts = useActiveGroupParts();

  return partErrors.filter((pE) =>
    parts.map((p) => p.id).includes(pE.partItemId)
  );
};

export const useCurrentGroupErrors = () => {
  const { materialGroupErrors } = useMaterialGroupErrorStore();
  const activeGroup = useActiveGroup();

  return materialGroupErrors.filter(
    (mge) => mge.materialGroupId === activeGroup?.id
  );
};

type MaterialGroupError = {
  materialGroupId: string;
  errorMessage: string;
};

type MaterialGroupErrors = {
  materialGroupErrors: MaterialGroupError[];
  addGroupError(materialGroupId: string, error: string): void;
  removeGroupErrors(materialGroupId: string): void;
};

export const useMaterialGroupErrorStore = create<MaterialGroupErrors>(
  (set) => ({
    materialGroupErrors: [],
    addGroupError: (materialGroupId, errorMessage) =>
      set((state) => ({
        materialGroupErrors: state.materialGroupErrors.concat({
          materialGroupId,
          errorMessage,
        }),
      })),
    removeGroupErrors: (materialGroupId: string) =>
      set((state) => ({
        materialGroupErrors: state.materialGroupErrors.filter(
          (materialGroupError) =>
            materialGroupError.materialGroupId !== materialGroupId
        ),
      })),
  })
);

export const usePartsHaveErrors = () => {
  const { materialGroupErrors } = useMaterialGroupErrorStore();
  const { partErrors } = usePartsErrorStore();
  const { nestingErrors } = useNestingErrorStore();

  return (
    materialGroupErrors.length > 0 ||
    partErrors.length > 0 ||
    nestingErrors.length > 0
  );
};

export const resetErrors = () => {
  useMaterialGroupErrorStore.setState({ materialGroupErrors: [] });
  usePartsErrorStore.setState({ partErrors: [], partWarnings: [] });
  useNestingErrorStore.setState({ nestingErrors: [] });
};
